import React, { useState, useEffect, useCallback } from 'react';
// import { useAuth } from '../../contexts/AuthContext';
import financialApi, { mockFinancialData } from '../../services/financialApi';
import demoFinancialData from '../../services/demoFinancialData';
import KpiCard from './KpiCard';
import ProductProfitabilityTable from './ProductProfitabilityTable';
import FinancialCharts from './FinancialCharts';
import PageLoader from '../common/PageLoader';
import './FinancialDashboard.css';

const DEMO_MODE_STORAGE_KEY = 'slipstream_financial_demo_mode';

function getInitialDemoMode() {
    try {
        const params = new URLSearchParams(window.location.search);
        if (params.get('demo') === 'true') return true;
        if (params.get('demo') === 'false') return false;
        return window.localStorage.getItem(DEMO_MODE_STORAGE_KEY) === 'true';
    } catch (e) {
        return false;
    }
}

const FinancialDashboard = () => {
    // const { user } = useAuth();
    const [dashboardData, setDashboardData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [lastRefresh, setLastRefresh] = useState(new Date());
    const [isDemoMode, setIsDemoMode] = useState(getInitialDemoMode);

    // Auto-refresh interval (5 minutes)
    const REFRESH_INTERVAL = 5 * 60 * 1000;

    const fetchFinancialData = useCallback(async () => {
        setLoading(true);
        setError(null);

        try {
            // Demo Mode shows a fully-populated preview (as if QuickBooks were
            // connected and COGS accounts confirmed) — explicit and toggleable,
            // never a silent fallback. Real backend data otherwise.
            if (isDemoMode) {
                await new Promise(resolve => setTimeout(resolve, 400));
                setDashboardData(demoFinancialData);
                setLastRefresh(new Date());
                setLoading(false);
                return;
            }

            const useMockData = false;

            if (useMockData) {
                // Simulate API delay
                await new Promise(resolve => setTimeout(resolve, 1000));
                setDashboardData(mockFinancialData);
                setLastRefresh(new Date());
                setLoading(false);
                return;
            }

            // Use the new aggregated dashboard data method that handles
            // fetching from multiple endpoints and transforming the data
            const result = await financialApi.getDashboardData();

            if (result.success && result.data) {
                console.log('Financial dashboard data fetched successfully:', result.data);
                setDashboardData(result.data);
                setLastRefresh(new Date());
            } else {
                throw new Error(result.error || 'Failed to fetch financial data');
            }
        } catch (err) {
            setError(`Failed to load financial data: ${err.message}`);
            console.error('Financial data fetch error:', err);

            // Fallback to mock data if backend fails (optional safety net)
            // Uncomment the following lines if you want to show mock data on error:
            // console.warn('Falling back to mock data due to API error');
            // setDashboardData(mockFinancialData);
        } finally {
            setLoading(false);
        }
    }, [isDemoMode]);

    const handleToggleDemoMode = useCallback(() => {
        setIsDemoMode(prev => {
            const next = !prev;
            try {
                window.localStorage.setItem(DEMO_MODE_STORAGE_KEY, String(next));
            } catch (e) {
                // localStorage unavailable — toggle still works for this session
            }
            return next;
        });
    }, []);

    // Manual refresh handler
    const handleRefresh = useCallback(() => {
        fetchFinancialData();
    }, [fetchFinancialData]);

    // Initial data load
    useEffect(() => {
        fetchFinancialData();
    }, [fetchFinancialData]);

    // Auto-refresh setup
    useEffect(() => {
        const interval = setInterval(fetchFinancialData, REFRESH_INTERVAL);
        return () => clearInterval(interval);
    }, [fetchFinancialData, REFRESH_INTERVAL]);

    // Format currency
    const formatCurrency = (value) => {
        // Check if value is a valid number before formatting
        if (typeof value !== 'number' || isNaN(value)) return 'N/A';

        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            maximumFractionDigits: 0
        }).format(value);
    };

    // Format percentage
    const formatPercentage = (value) => {
        // Check if value is a valid number before formatting
        if (typeof value !== 'number' || isNaN(value)) return 'N/A';
        return `${value.toFixed(1)}%`;
    };

    // Export data to CSV
    const handleExportCSV = () => {
        // Mandatory check for non-empty array
        if (!dashboardData?.productProfitability?.length) {
            console.warn('Cannot export CSV: No product profitability data found.');
            return;
        }

        const headers = ['Product Name', 'Revenue', 'Cost', 'Profit Margin (%)', 'Units Sold', 'Profit per Unit'];
        const csvContent = [
            headers.join(','),
            ...dashboardData.productProfitability.map(product => [
                `"${product.productName}"`,
                product.revenue,
                product.cost,
                product.profitMargin,
                product.unitsSold,
                // Optional chaining for properties, though less critical here
                product.profitPerUnit?.toFixed(2)
            ].join(','))
        ].join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `financial-data-${new Date().toISOString().split('T')[0]}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
    };

    if (loading) {
        return (
            <PageLoader 
                title="Loading financial data..." 
                message="" 
            />
        );
    }

    if (error) {
        return (
            <div className="financial-dashboard">
                <div className="dashboard-header">
                    <h1>Financial Dashboard</h1>
                </div>
                <div className="error-container">
                    <div className="error-message">
                        <h3>Error Loading Dashboard</h3>
                        <p>{error}</p>
                        <button className="btn-primary" onClick={handleRefresh}>
                            Try Again
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    // Final check for overall data presence after loading and error checks
    if (!dashboardData || !dashboardData.kpiData) {
        return (
            <div className="financial-dashboard">
                <div className="dashboard-header">
                    <h1>Financial Dashboard</h1>
                </div>
                <div className="no-data-container">
                    <h3>No Data Available</h3>
                    <p>Financial data is currently unavailable. Please try refreshing.</p>
                    <button className="btn-primary" onClick={handleRefresh}>
                        Refresh
                    </button>
                </div>
            </div>
        );
    }

    // Destructure for cleaner access in rendering logic
    const { kpiData, productProfitability, chartData } = dashboardData;
    const hasKpiData = kpiData && Object.keys(kpiData).length > 0;
    const hasProfitabilityData = productProfitability?.length > 0;

    return (
        <div className="financial-dashboard">
            {/* Dashboard Header */}
            <div className="dashboard-header">
                <div className="header-content">
                    <h1>
                        Financial Analysis Dashboard
                        {isDemoMode && <span className="demo-mode-badge">Preview Data</span>}
                    </h1>
                    <p className="dashboard-subtitle">
                        {isDemoMode
                            ? 'Illustrative preview of Financial Analysis once QuickBooks is fully connected'
                            : 'Real-time financial insights and performance metrics'}
                    </p>
                </div>
                <div className="dashboard-actions">
                    <div className="dashboard-meta">
                        <span className="last-updated">
                            Last updated: {lastRefresh.toLocaleTimeString()}
                        </span>
                        <label className="demo-mode-toggle" title="Show an illustrative preview instead of live data">
                            <input
                                type="checkbox"
                                checked={isDemoMode}
                                onChange={handleToggleDemoMode}
                            />
                            Preview mode
                        </label>
                    </div>
                    <div className="action-buttons">
                        <button
                            className="btn-secondary"
                            onClick={handleExportCSV}
                            title="Export data to CSV"
                            // Disable button if there's no data to export
                            disabled={!hasProfitabilityData}
                        >
                            Export CSV
                        </button>
                        <button
                            className="btn-primary"
                            onClick={handleRefresh}
                            title="Refresh data"
                        >
                            Refresh
                        </button>
                    </div>
                </div>
            </div>

            {/* Hero summary — the headline number, up front */}
            {hasKpiData && kpiData.totalRevenue?.value != null && (
                <div className="financial-hero">
                    <div className="financial-hero-figure">
                        <span className="financial-hero-label">Total Revenue</span>
                        <span className="financial-hero-value">{formatCurrency(kpiData.totalRevenue.value)}</span>
                        {typeof kpiData.totalRevenue.change === 'number' && (
                            <span className={`financial-hero-change ${kpiData.totalRevenue.trend === 'down' ? 'is-down' : 'is-up'}`}>
                                {kpiData.totalRevenue.change > 0 ? '+' : ''}{kpiData.totalRevenue.change}% year over year
                            </span>
                        )}
                    </div>
                    {kpiData.grossMargin?.value != null && (
                        <div className="financial-hero-stat">
                            <span className="financial-hero-stat-value">{formatPercentage(kpiData.grossMargin.value)}</span>
                            <span className="financial-hero-stat-label">Gross Margin</span>
                        </div>
                    )}
                    {kpiData.netProfitMargin?.value != null && (
                        <div className="financial-hero-stat">
                            <span className="financial-hero-stat-value">{formatPercentage(kpiData.netProfitMargin.value)}</span>
                            <span className="financial-hero-stat-label">Net Margin</span>
                        </div>
                    )}
                </div>
            )}

            {/* Key Performance Indicators */}
            {/* Check: Ensure kpiData object is not null/empty */}
            {hasKpiData && (
                <section className="dashboard-section">
                    <h2>Key Performance Indicators</h2>

                    <h3 className="kpi-group-heading">Revenue &amp; Margin</h3>
                    <div className="kpi-grid">
                        {/* Mandatory Check: Use optional chaining (?.) for all nested values */}
                        <KpiCard
                            title="Total Revenue"
                            value={kpiData.totalRevenue?.value == null ? null : formatCurrency(kpiData.totalRevenue.value)}
                            change={kpiData.totalRevenue?.change}
                            trend={kpiData.totalRevenue?.trend}
                            source={kpiData.totalRevenue?.source}
                            icon="💰"
                        />
                        <KpiCard
                            title="Net Profit Margin"
                            value={kpiData.netProfitMargin?.value == null ? null : formatPercentage(kpiData.netProfitMargin.value)}
                            change={kpiData.netProfitMargin?.change}
                            trend={kpiData.netProfitMargin?.trend}
                            source={kpiData.netProfitMargin?.source}
                            icon="📈"
                        />
                        <KpiCard
                            title="Cost of Goods Sold"
                            value={kpiData.costOfGoodsSold?.value == null ? null : formatCurrency(kpiData.costOfGoodsSold.value)}
                            change={kpiData.costOfGoodsSold?.change}
                            trend={kpiData.costOfGoodsSold?.trend}
                            source={kpiData.costOfGoodsSold?.source}
                            icon="📦"
                        />
                        <KpiCard
                            title="Gross Margin"
                            value={kpiData.grossMargin?.value == null ? null : formatPercentage(kpiData.grossMargin.value)}
                            change={kpiData.grossMargin?.change}
                            trend={kpiData.grossMargin?.trend}
                            source={kpiData.grossMargin?.source}
                            icon="💹"
                        />
                    </div>

                    <h3 className="kpi-group-heading">Efficiency</h3>
                    <div className="kpi-grid">
                        <KpiCard
                            title="Inventory Turnover"
                            value={kpiData.inventoryTurnover?.value == null ? null : kpiData.inventoryTurnover.value.toFixed(1)}
                            change={kpiData.inventoryTurnover?.change}
                            trend={kpiData.inventoryTurnover?.trend}
                            source={kpiData.inventoryTurnover?.source}
                            icon="🔄"
                        />
                        <KpiCard
                            title="Days Sales Outstanding"
                            value={kpiData.daysSalesOutstanding?.value == null ? null : `${Math.round(kpiData.daysSalesOutstanding.value)} days`}
                            change={kpiData.daysSalesOutstanding?.change}
                            trend={kpiData.daysSalesOutstanding?.trend}
                            source={kpiData.daysSalesOutstanding?.source}
                            icon="📅"
                        />
                        <KpiCard
                            title="Outstanding Receivables"
                            value={kpiData.outstandingReceivables?.value == null ? null : formatCurrency(kpiData.outstandingReceivables.value)}
                            change={kpiData.outstandingReceivables?.change}
                            trend={kpiData.outstandingReceivables?.trend}
                            source={kpiData.outstandingReceivables?.source}
                            icon="💵"
                        />
                        <KpiCard
                            title="Outstanding Payables"
                            value={kpiData.outstandingPayables?.value == null ? null : formatCurrency(kpiData.outstandingPayables.value)}
                            change={kpiData.outstandingPayables?.change}
                            trend={kpiData.outstandingPayables?.trend}
                            source={kpiData.outstandingPayables?.source}
                            icon="📤"
                        />
                    </div>
                </section>
            )}
            {/* Financial Charts */}
            {/* Check: Ensure chartData is not null/empty before rendering component */}
            {chartData && Object.keys(chartData).length > 0 && (
                <section className="dashboard-section">
                    <h2>Financial Analytics & Trends</h2>
                    {!chartData.revenueData?.connected && (
                        <p className="chart-placeholder-note">
                            Connect QuickBooks (Settings → Integrations) to see real revenue trends here.
                        </p>
                    )}
                    {chartData.revenueData?.connected && chartData.monthlyProfitLoss?.costsAvailable === false && (
                        <p className="chart-placeholder-note">
                            Cost and profit trend lines will appear once COGS accounts are confirmed
                            (Settings → Integrations).
                        </p>
                    )}
                    <FinancialCharts chartData={chartData} />
                </section>
            )}

            {/* Product Profitability Analysis */}
            {/* Check: Ensure productProfitability is a non-empty array */}
            {hasProfitabilityData && (
                <section className="dashboard-section">
                    <h2>Product Profitability Analysis</h2>
                    <ProductProfitabilityTable
                        data={productProfitability}
                        onExport={handleExportCSV}
                    />
                </section>
            )}
        </div>
    );
};

export default FinancialDashboard;